/* eslint-disable no-await-in-loop, padded-blocks, no-empty-pattern */
/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import api from '@/api';
import Vue from 'vue';

export const namespaced = true;

function initializePlanDetails() {
  return {
    locationWatershedContext: null,
    prioritizationContext: null,
    projectRationale: null,
    alternatives: null,
    recommendations: null,
    projectActions: null,
    projectEvaluation: null,
    scheduleOfActivities: null,
    category: null,
  };
}

function initializeProjectBudget(projectId) {
  return {
    projectId,
    projectBudgetYears: [],
    projectBudgetDecreases: [],
  };
}

function initializePlanDetailsWithProjectId(projectId, projectCategory) {
  const pd = initializePlanDetails();
  pd.projectId = projectId;
  /* eslint-ignore-next-line */
  if (projectCategory === 'ECREP Project' || projectCategory === 'Habitat Conservation Project' || projectCategory === 'Invasive Treatment Project' || projectCategory === 'VEGBAC Project' || projectCategory === 'Conservation Easement') {
    pd.projectActions = `
      <strong>Site Prep</strong>
      <p>Invasive plants will be addressed prior to all other site activities to ensure efficacy of native plant establishment and provide the greatest maintenance efficiency over the life of the project. Typically, invasive blackberry and english ivy can be brought under reasonable control within 1-2 years from initial treatment. Invasive grasses can take two or more years of mechanical and chemical control depending on the age of the stand.  English holly, hawthorn and laurel can be controlled by cutting and stump treatment. During site prep, portions of the site heavily dominated by invasive species will appear mostly brown and sparsely vegetated.</p>
      <strong>Planting</strong>
      <p>Most of the herbaceous and woody plants will be installed during the fall of [      ] and winter of [      ]. In general, the plant palette will reflect reference sites for intact riparian ecosystems in the Tualatin basin, and more locally in neighboring remnant or intact riparian buffers connected to or near the project site. Additional understory herbaceous plants will be planted on an ongoing basis as the invasive vegetation is brought under control and the site matures.</p>
      <p>Typical woody plants used include (actual plants used may vary due to availability and reference sites):</p>
      <p>Trees and Shrubs:</p>
      <ul>
        <li>Western red cedar (Thuja plicata)</li>
        <li>W. Valley Ponderosa pine (Pinus ponderosa (W.v.))</li>
        <li>Big leaf maple (Acer macrophyllum)</li>
        <li>Red alder (Alnus rubra)</li>
        <li>Scouler's willow (Salix scouleriana)</li>
        <li>Pacific willow (Salix lasiandra)</li>
        <li>Indian plum (Oemleria cerasiformis)</li>
        <li>Pacific ninebark (Physocarpus capitatus)</li>
        <li>Cascara (Rhamnus purshiana)</li>
        <li>Salmonberry (Rubus spectabilis)</li>
        <li>Red elderberry (Sambucus racemosa)</li>
        <li>Snowberry (Symphoricarpos albus)</li>
        <li>Thimbleberry (Rubus parviflorus)</li>
        <li>Spiraea (Spiraea douglasii)</li>
        <li>Twinberry (Lonicera involucrate)</li>
      </ul>
      <p>Herbaceous plants:</p>
      <ul>
        <li>American water-plantain (Alisma plantago-aquatica)</li>
        <li>Water Foxtail (Alopecurus geniculatus)</li>
        <li>American slough grass (Beckmannia syzigachne)</li>
        <li>Creeping spike rush (Eleocharis palustris)</li>
        <li>Fragrant popcorn flower (Plagiobothrys figuratus)</li>
        <li>Willow dock (Rumex salicifolius)</li>
        <li>Western mannagrass (Glyceria occidentalis)</li>
        <li>Tufted hairgrass (Deschampsia cespitosa)</li>
        <li>Dense sedge (Carex densa)</li>
        <li>One-sided sedge (Carex unilateralis)</li>
      </ul>
      <p>A detailed plant list made up of selections from the above list will be provided as the planting plan is developed.  Planting plans are completed the summer prior to planting.</p>
      <strong>Maintenance</strong>
      <p>Interplanting and spot treatment of weeds will follow the initial planting, with most major activities anticipated to be finished by the end of year five. Following initial site preparation, herbicide applications will be limited to spot sprays and will taper off as the native plant community establishes. Following establishment, site entries by contractors will be minimal and mostly focus on monitoring and removal of additional invasive plants.</p>
    `;
  }
  if (['ECREP Project', 'Habitat Conservation Project', 'Invasive Treatment Project', 'VEGBAC Project', 'Conservation Easement'].includes(projectCategory)) {
    pd.scheduleOfActivities = `
    <ul>
      <li>Site Preparation – <strong><em>[add date range]</em></strong>
        <ul>
          <li>Summer: Cut grass and invasive plants during the first year and use herbicide to treat difficult to control weeds in subsequent years
          <li>Fall: Herbicide application to treat invasive plants and cut and treat invasive trees
          <li>Spring: Herbicide application to treat invasive plants
        </ul>
      </li>
      <li>Planting – <strong><em>[add date range]</em></strong>
        <ul>
          <li>Fall: Seed grasses, sedges and flowers into areas where invasive plants were removed during site preparation</li>
          <li>Winter: Plant trees and shrubs into the project area</li>
        </ul>
      </li>
      <li>Maintenance – <strong><em>[add date range]</em></strong>
        <ul>
          <li>Spring: If competitive grasses are growing around the new plants, use herbicide to treat grasses in a ring around the plants and spot spray invasive plants</li>
          <li>Fall: Use herbicide to treat invasive plants in the project area</li>
          <li>Winter: If needed, plant additional trees and shrubs in areas with low survival from the initial planting.</li>
        </ul>
      </li>
    </ul>`;
  }
  return pd;
}

export const state = {
  project: null,
  projectStatus: null,
  projectLoading: false,
  projectSnapshotDraftLoading: false,
  projectGeometry: null,
  projectSites: null,
  projectSitesLoading: false,
  projectBudget: { projectBudgetDecreases: [], projectBudgetYears: [] },
  projectBudgetYearSnapshots: [],
  projectBudgetSummaryDataLoaded: false,
  annualBudgetHistoryItems: {},
  pendingBudgets: [],
  loadingAnnualBudgetHistoryItems: {},
  projectSnapshot: {},
  projectSnapshots: [],
  projectSnapshotDraft: {},
  cancelledCompletedProjectSnapshots: [],
  projectFiscalYearSnapshots: [],
  currentProjectSnapshots: [],
  resourcePriorities: null,
  serverError: null,
  successMessage: null,
  decreaseSuccessMessage: null,
  errorMessage: null,
  decreaseErrorMessage: null,
  currentPlanDetails: initializePlanDetails(),
  siteContactsLoading: [],
  projectContacts: [],
  siteOrganizationsLoading: [],
  projectOrganizations: [],
  priceLists: [],
  fundingSourceEncumberedFunds: [],
  fundingSourceExpendedFunds: [],
  encumberedFundsForApprovedBudgets: [],
  fundingSources: [],
  fundsPerFiscalYear: { year: null, funds: [] },
  currentPriceLists: [],
  currentPriceListsLoading: false,
  products: [],
  projectAccomplishmentTargetMonitoringEvents: [],
  projectAccomplishmentTargetMonitoringEventEvaluationMethods: [],
  projectGoals: [],
  projectGoalSelections: [],
  projectAccomplishmentTargets: [],
  projectObjectiveAccomplishmentTargetSelections: [],
  projectObjectives: [],
  projectObjectiveSelections: [],
  projectPractices: [],
  projectWorkorders: [],
  projectWorkorderBill: null,
  projectWorkorderBills: [],
  projectWorkorderLineItemFundingSources: [],
  practices: [],
  practiceComponents: [],
  targetSeasons: [],
  incentiveTypes: [],
  projectIncentivePayments: [],
  projectConcept: null,
  projectResourcePriorities: [],
  dashboardData: [],
  dashboardLoading: false,
  fundingSourcesLoading: false,
  hasOpenWorkOrders: false,
  hasOpenWorkOrderBills: false,
  projectsPendingApproval: [],
  myActiveProjects: [],
  planDetailsLoading: false,
  productPriceListsCache: new Map(),
};

export const actions = {
  async getProjectBudget({ commit }) {
    commit('SET_DASHBOARD_LOADING', true);
    await api.get('/projectBudget').then((response) => {
      commit('SET_DASHBOARD_LOADING', false);
      return commit('SET_DASHBOARD_DATA', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
      commit('SET_DASHBOARD_LOADING', false);
    });
  },
  async fetchAnnualBudgetHistoryItems({ commit }, id) {
    commit('SET_ANNUAL_HISTORY_IS_LOADING', id);

    api.get(
      `/projectBudget/annualBudgetHistory/${id}`,
    ).then(async (response) => {
      commit('SET_ANNUAL_HISTORY_ITEMS', { id, historyItems: response.data });
    }).finally(() => {
      commit('SET_ANNUAL_HISTORY_NOT_LOADING', id);
    });
  },
  async fetchPendingBudgets({ commit }) {
    await api.get('/projectBudget/pendingannualbudgets').then(async (response) => {
      commit('SET_PENDING_BUDGETS', response.data);
    });
  },
  async getProject({ commit }, projectId) {
    commit('SET_PROJECT_LOADING', true);
    commit('SET_PROJECT', null);
    return api.get(`/project/${projectId}`).then((response) => {
      if (response.status === 200) {
        commit('SET_SUCCESS_MESSAGE', 'The project was successfully read.');
      }
      commit('SET_PROJECT_LOADING', false);
      return commit('SET_PROJECT', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
      commit('SET_PROJECT_LOADING', false);
    });
  },
  async getProjectSnapshot({ commit }, projectId) {
    await api.get(`/project/${projectId}/snapshot`).then(
      (response) => commit('SET_PROJECT_SNAPSHOT', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getProjectSnapshots({ commit }, fiscalYear) {
    await api.get(`/projectSnapshots/${fiscalYear}`).then(
      (response) => commit('SET_PROJECT_SNAPSHOTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getProjectDraftSnapshot({ commit }, projectId) {
    commit('SET_PROJECT_DRAFT_SNAPSHOT_LOADING', true);
    await api.get(`/projectSnapshots/Drafts/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_DRAFT_SNAPSHOT_LOADING', false);
        return commit('SET_PROJECT_DRAFT_SNAPSHOT', response.data);
      },
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
      commit('SET_PROJECT_DRAFT_SNAPSHOT_LOADING', false);
    });
  },
  async getCancelledCompletedProjectSnapshots({ commit }, fiscalYear) {
    await api.get(`/projectSnapshots/byfiscalyearcancellledcompleted/${fiscalYear}`).then(
      (response) => commit('SET_CANCELLED_COMPLETED_PROJECT_SNAPSHOTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getProjectSnapshotsByFiscalYear({ commit }, fiscalYear) {
    await api.get(`/projectSnapshots/byfiscalyear/${fiscalYear}`).then(
      (response) => commit('SET_PROJECT_FISCALYEAR_SNAPSHOTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getCurrentProjectSnapshot({ commit }, projectId) {
    await api.get(`/projectSnapshots/byprojectid/${projectId}`).then(
      (response) => commit('SET_CURRENT_PROJECT_SNAPSHOTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async anyOpenWorkOrders({ commit }, projectId) {
    await api.get(`/project/${projectId}/anyOpenWorkOrders`).then((response) => {
      commit('SET_HAS_OPEN_WORK_ORDERS', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async anyOpenWorkOrderBills({ commit }, projectId) {
    await api.get(`/project/${projectId}/anyOpenWorkOrderBills`).then((response) => {
      commit('SET_HAS_OPEN_WORK_ORDER_BILLS', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async setProjectAttributes({ commit }, project) {
    commit('SET_ERROR_MESSAGE', null);
    await api.put(`/project/${project.id}`, project).then(async () => {
    }).catch((exception) => {
      console.error('Enter api.put /project catch()');
      console.error(exception);
      commit('SET_ERROR_MESSAGE', exception);
      console.error('Exit api.put /project catch()');
    });
  },
  async setProjectApprovalStatus({ commit }, params) {
    commit('SET_ERROR_MESSAGE', null);
    await api.put(`/project/${params.id}/approvalStatus/${params.status}`).then(async () => {
    }).catch((exception) => {
      console.error('Enter api.put /project/{id}/approvalStatus/{status} catch()');
      console.error(exception);
      commit('SET_ERROR_MESSAGE', exception);
      console.error('Exit api.put /project/{id}/approvalStatus/{status} catch()');
    });
  },
  async setProjectStatus({ commit }, params) {
    commit('SET_ERROR_MESSAGE', null);
    await api.put(`/project/${params.id}/status/${params.status}`).then(async () => {
    }).catch((exception) => {
      console.error('Enter api.put /project/{id}/status/{status} catch()');
      console.error(exception);
      commit('SET_ERROR_MESSAGE', exception);
      console.error('Exit api.put /project/{id}/status/{status} catch()');
    });
  },
  async setProjectOutcomeSummary({ commit }, model) {
    commit('SET_ERROR_MESSAGE', null);
    await api.put(`/project/${model.id}/outcomeSummary`, model).then(async () => {
    }).catch((exception) => {
      console.error('Enter api.put /project/{id}/outcomeSummary catch()');
      console.error(exception);
      commit('SET_ERROR_MESSAGE', exception);
      console.error('Exit api.put /project/{id}/outcomeSummary catch()');
    });
  },
  async getProjectSites({ commit }, projectId) {
    commit('SET_PROJECT_SITES_LOADING', true);
    await api.get(`/projectsite/project/${projectId}`).then(async (response) => {
      commit('SET_PROJECT_SITES', response.data);
      commit('SET_PROJECT_SITES_LOADING', false);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
      commit('SET_PROJECT_SITES_LOADING', false);
    });
  },
  async getProjectContacts({ state, commit }, siteId) {
    if (!state.projectContacts.map((pc) => pc.siteId).includes(siteId)) {
      commit('ADD_SITE_CONTACTS_LOADING', siteId);
      await api.get(`/projectsite/site/${siteId}/contacts`)
        .then(async (response) => {
          const siteContacts = { siteId, contacts: response.data };
          commit('ADD_PROJECT_CONTACTS', siteContacts);
        }).catch((exception) => {
          console.error(exception);
          commit('SET_SERVER_ERROR', exception);
        }).finally(() => commit('REMOVE_SITE_CONTACTS_LOADING', siteId));
    }
  },
  async getProjectOrganizations({ state, commit }, siteId) {
    if (!state.projectOrganizations.map((pc) => pc.siteId).includes(siteId)) {
      commit('ADD_SITE_ORGANIZATIONS_LOADING', siteId);
      await api.get(`/projectsite/site/${siteId}/organizations`)
        .then(async (response) => {
          const siteOrganizations = { siteId, organizations: response.data };
          commit('ADD_PROJECT_ORGANIZATIONS', siteOrganizations);
        }).catch((exception) => {
          console.error(exception);
          commit('SET_SERVER_ERROR', exception);
        }).finally(() => commit('REMOVE_SITE_ORGANIZATIONS_LOADING', siteId));
    }
  },
  async setProjectSites({ commit }, parameters) {
    commit('SET_ERROR_MESSAGE', null);
    const exceptions = [];
    const projectSites = parameters.projectSites.map(
      (projectSite) => JSON.parse(JSON.stringify(projectSite)),
    );
    let projectSite = projectSites.pop();
    while (projectSite) {
      const projectSiteModel = {
        recordName: '',
        relatedProjectId: parameters.projectId,
        relatedSiteId: projectSite.attributes.insightlyid,
      };
      await api.post('/projectsite', projectSiteModel).then(async () => {
      }).catch((exception) => {
        console.error('Enter api.post /projectsite catch()');
        console.error(exception);
        exceptions.push(exception);
        console.error('Exit api.post /projectsite catch()');
      });
      projectSite = projectSites.pop();
    }
    if (exceptions.length === 0) {
      commit('SET_SUCCESS_MESSAGE', 'The project\'s sites were successfully updated.');
    } else {
      commit('SET_ERROR_MESSAGE', 'The project\'s sites were not successfully updated.');
    }
  },
  async deleteProjectSites({ commit }, projectSiteIds) {
    commit('SET_ERROR_MESSAGE', null);
    const exceptions = [];
    let id = projectSiteIds.pop();
    while (id) {
      await api.delete(`/projectsite/${id}`).then(async () => {
      }).catch((exception) => {
        console.error('Enter api.delete /projectsite catch()');
        console.error(exception);
        exceptions.push(exception);
        console.error('Exit api.delete /projectsite catch()');
      });
      id = projectSiteIds.pop();
    }
    if (exceptions.length === 0) {
      commit('SET_SUCCESS_MESSAGE', 'The project\'s sites were successfully deleted.');
    } else {
      commit('SET_ERROR_MESSAGE', 'The project\'s sites were not successfully deleted.');
    }
  },
  async fetchPlanDetails({ commit, state }, projectId) {
    commit('SET_SERVER_ERROR', '');
    if (!state.currentPlanDetails || state.currentPlanDetails.projectId !== projectId) {
      await api.get(`/planDetails/${projectId}`)
        .then((response) => commit('SET_PLAN_DETAILS', response.data))
        .catch((error) => {
          commit('SET_SERVER_ERROR', error);
          return commit('SET_PLAN_DETAILS', initializePlanDetailsWithProjectId(projectId, state.project.category));
        });
    }
    return commit('SET_PLAN_DETAILS', state.currentPlanDetails);
  },
  async forceFetchPlanDetails({ commit, state }, projectId) {
    commit('SET_SERVER_ERROR', '');
    await api.get(`/planDetails/${projectId}`).then(
      (response) => commit('SET_PLAN_DETAILS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
      return commit('SET_PLAN_DETAILS', initializePlanDetailsWithProjectId(projectId, state.project.category));
    });
    return commit('SET_PLAN_DETAILS', state.currentPlanDetails);
  },
  async fetchProjectBudget({ commit, state }, projectId) {
    commit('SET_SERVER_ERROR', '');
    if (!state.projectBudget || state.projectBudget.projectId !== projectId) {
      await api.get(`/projectBudget/${projectId}`)
        .then((response) => commit('SET_PROJECT_BUDGET', response.data))
        .catch((error) => {
          console.error(error);
          commit('SET_SERVER_ERROR', error);
          return commit('SET_PROJECT_BUDGET', initializeProjectBudget(projectId));
        });
    }
    return commit('SET_PROJECT_BUDGET', state.projectBudget);
  },
  async forceFetchProjectBudget({ commit }, projectId) {
    commit('SET_SERVER_ERROR', '');
    await api.get(`/projectBudget/${projectId}`)
      .then((response) => commit('SET_PROJECT_BUDGET', response.data))
      .catch((error) => {
        console.error(error);
        commit('SET_SERVER_ERROR', error);
        return commit('SET_PROJECT_BUDGET', initializeProjectBudget(projectId));
      });
  },
  async setProjectBudgetSummaryDataLoaded({ commit }, dataLoaded) {
    commit('SET_PROJECT_BUDGET_SUMMARY_DATA_LOADED', dataLoaded);
  },
  async fetchPriceLists({ commit, state }) {
    if (!state.priceLists || state.priceLists.length === 0) {
      await api.get('/project/pricelists')
        .then((response) => commit('SET_PRICELISTS', response.data))
        .catch((error) => {
          console.error(error);
        });
    }
    return commit('SET_PRICELISTS', state.priceLists);
  },
  async fetchFundingSources({ commit, state }) {
    if (!state.fundingSources || state.fundingSources.length === 0) {
      commit('SET_FUNDING_SOURCES_LOADING', true);
      await api.get('/project/fundingSources')
        .then((response) => commit('SET_FUNDING_SOURCES', response.data))
        .catch((error) => {
          console.error(error);
        }).finally(() => {
          commit('SET_FUNDING_SOURCES_LOADING', false);
        });
    }
    return commit('SET_FUNDING_SOURCES', state.fundingSources);
  },
  async fetchProducts({ commit, state }) {
    if (!state.products || state.products.length === 0) {
      await api.get('/project/products')
        .then((response) => commit('SET_PRODUCTS', response.data))
        .catch((error) => {
          console.error(error);
        });
    }
    return commit('SET_PRICELISTS', state.priceLists);
  },
  async fetchProductPriceListEntries({ commit, state }, productId) {
    try {
      if (!state.productPriceListsCache.has(productId)) {
        commit('SET_CURRENT_PRICE_LISTS_LOADING', true);

        const { data: entries } = await api.get(`/project/product/${productId}/entries`);
        state.productPriceListsCache.set(productId, entries.map((entry) => ({
          priceList: state.priceLists.find((priceList) => priceList.pricebooK_ID === entry.pricebooK_ID),
          entry,
        })));

        commit('SET_CURRENT_PRICE_LISTS_LOADING', false);
      }

      return commit('SET_CURRENT_PRICE_LISTS', state.productPriceListsCache.get(productId));
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  async savePlanDetails({ commit }, planDetails) {
    commit('SET_SUCCESS_MESSAGE', '');
    if (planDetails.id) {
      await api.put('/planDetails', planDetails).then(async (response) => {
        commit('SET_SUCCESS_MESSAGE', 'The Plan Details were successfully updated.');
        commit('SET_PLAN_DETAILS', response.data);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    } else {
      await api.post('/planDetails', planDetails).then(async (response) => {
        commit('SET_SUCCESS_MESSAGE', 'The Plan Details were successfully written.');
        commit('SET_PLAN_DETAILS', response.data);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    }
  },
  async saveProjectBudget({ commit, state }) {
    commit('SET_SUCCESS_MESSAGE', '');
    if (state.projectBudget.id) {
      await api.put('/projectBudget', state.projectBudget).then(async (response) => {
        commit('SET_SUCCESS_MESSAGE', 'The Project Budget was successfully updated.');
        commit('SET_PROJECT_BUDGET', response.data);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    } else {
      await api.post('/projectBudget', state.projectBudget).then(async (response) => {
        commit('SET_SUCCESS_MESSAGE', 'The Project Budget was  successfully written.');
        commit('SET_PROJECT_BUDGET', response.data);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    }
  },
  async addAnnualBudgetIncrease({ commit }, adjustment) {
    commit('SET_ERROR_MESSAGE', '');
    commit('SET_SUCCESS_MESSAGE', '');
    await api.post('/projectBudget/annualBudgetIncrease', adjustment).then(async () => {
      commit('SET_SUCCESS_MESSAGE', 'The Annual Budget Adjustment was successfully saved.');
    }).catch((error) => commit('SET_ERROR_MESSAGE', error));
  },
  async setDecreaseErrorMessage({ commit }, errorMessage) {
    commit('SET_DECREASE_ERROR_MESSAGE', errorMessage);
  },
  async addProjectDecrease({ commit }, adjustment) {
    commit('SET_DECREASE_ERROR_MESSAGE', '');
    commit('SET_DECREASE_SUCCESS_MESSAGE', '');
    await api.post('/projectBudget/budgetDecrease', adjustment).then(async () => {
      commit('SET_DECREASE_SUCCESS_MESSAGE', 'The Annual Budget Adjustment was successfully saved.');
    }).catch((error) => commit('SET_DECREASE_ERROR_MESSAGE', error));
  },
  async saveProjectBudgetLineItem({ commit }, lineItem) {
    commit('SET_SUCCESS_MESSAGE', '');
    if (lineItem.id) {
      await api.put('/projectBudget/lineItem', lineItem).then(async () => {
        commit('SET_SUCCESS_MESSAGE', 'The Line Item was successfully written.');
        commit('SET_EXISTING_BUDGET_LINE_ITEM', lineItem);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    } else {
      await api.post('/projectBudget/lineItem', lineItem).then(async (response) => {
        commit('SET_SUCCESS_MESSAGE', 'The Line Item was successfully written.');
        commit('SET_PROJECT_BUDGET_LINE_ITEM', response.data);
      }).catch((error) => commit('SET_ERROR_MESSAGE', error));
    }
  },
  async updateProjectBudgetYear({ commit }, { year, projectSnapshotId }) {
    commit('SET_SUCCESS_MESSAGE', '');
    await api.put(`/projectBudget/year/${projectSnapshotId}`, year).then(async () => {
      commit('SET_SUCCESS_MESSAGE', 'The Annual Budget has been updated.');
    }).catch((error) => commit('SET_ERROR_MESSAGE', error));
  },
  async updateProjectBudgetYearContingency({ commit }, { year, newContingencyPercent }) {
    commit('SET_SUCCESS_MESSAGE', '');
    const updatedYear = year;
    updatedYear.contingencyPercent = newContingencyPercent;
    await api.put('/projectBudget/year', updatedYear).then(async (response) => {
      commit('SET_PROJECT_BUDGET_YEAR_CONTINGENCY', response.data);
      commit('SET_SUCCESS_MESSAGE', 'The Contingency Amount has been updated.');
    }).catch((error) => commit('SET_ERROR_MESSAGE', error));
  },
  async deleteProjectBudgetLineItem({ commit }, lineItem) {
    commit('SET_SUCCESS_MESSAGE', '');
    await api.delete(`/projectBudget/lineItem/${lineItem.id}`).then(async () => {
      commit('SET_SUCCESS_MESSAGE', 'The Line Item was removed.');
      commit('DELETE_PROJECT_BUDGET_LINE_ITEM', lineItem);
    }).catch((error) => commit('SET_ERROR_MESSAGE', error));
  },
  setProjectGeometry({ commit }, projectGeometry) {
    return commit('SET_PROJECT_GEOMETRY', projectGeometry);
  },
  async getProjectResourcePriorities({ commit }) {
    await api.get('/projectlookup/projectresourcepriority').then(
      (response) => {
        commit('SET_PROJECT_RESOURCE_PRIORITIES', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectAccomplishmentTargetMonitoringEvents({ commit }, projectId) {
    await api.get(`/projectlookup/projectAccomplishmentTargetMonitoringEvent/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_ACCOMPLISHMENT_TARGET_MONITORING_EVENTS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectAccomplishmentTargetMonitoringEventEvaluationMethods({ commit }) {
    await api.get('/projectlookup/monitoringEventEvaluationMethods').then(
      (response) => {
        commit('SET_PROJECT_ACCOMPLISHMENT_TARGET_MONITORING_EVENT_EVALUATION_METHODS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectGoalsAsync({ commit }) {
    await api.get('/projectlookup/projectgoal').then(
      (response) => {
        commit('SET_PROJECT_GOALS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectGoalSelectionsAsync({ commit }, projectId) {
    await api.get(`/projectlookup/projectgoalselection/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_GOAL_SELECTIONS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectObjectivesAsync({ commit }) {
    await api.get('/projectlookup/projectobjective').then(
      (response) => {
        commit('SET_PROJECT_OBJECTIVES', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectAccomplishmentTargetsAsync({ commit }) {
    await api.get('/projectlookup/projectaccomplishmenttargets').then(
      (response) => {
        commit('SET_PROJECT_ACCOMPLISHMENT_TARGETS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getValidProjectAccomplishmentTargetsAsync({ commit }) {
    await api.get('/projectlookup/validprojectaccomplishmenttargets').then(
      (response) => {
        commit('SET_PROJECT_ACCOMPLISHMENT_TARGETS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getValidProjectObjectivesAsync({ commit }) {
    await api.get('/projectlookup/validprojectobjective').then(
      (response) => {
        commit('SET_PROJECT_OBJECTIVES', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getValidProjectGoalsAsync({ commit }) {
    await api.get('/projectlookup/validprojectgoal').then(
      (response) => {
        commit('SET_PROJECT_GOALS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectObjectiveAccomplishmentTargetSelectionsAsync({ commit }, projectId) {
    await api.get(`/projectlookup/projectobjectiveaccomplishmenttargetselection/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_OBJECTIVE_ACCOMPLISHMENT_TARGET_SELECTIONS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getValidProjectObjectiveAccomplishmentTargetSelectionsAsync({ commit }, projectId) {
    await api.get(`/projectlookup/validprojectobjectiveaccomplishmenttargetselection/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_OBJECTIVE_ACCOMPLISHMENT_TARGET_SELECTIONS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectObjectiveSelectionsAsync({ commit }, projectId) {
    await api.get(`/projectlookup/projectobjectiveselection/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_OBJECTIVE_SELECTIONS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async setProjectAccomplishmentTargetMonitoringEvent({ commit }, model) {
    await api.post(`/projectlookup/projectAccomplishmentTargetMonitoringEvent/${model.projectId}`, model).catch(
      (exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      },
    );
  },
  async setProjectGoalSelectionsAsync({ commit }, params) {
    await api.post(`/projectlookup/projectGoalSelection/${params.projectId}`, params.selections).catch(
      (exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      },
    );
    commit('SET_PROJECT_GOAL_SELECTIONS', params.selections);
  },
  async setProjectObjectiveAccomplishmentTargetSelectionAsync({ commit }, params) {
    await api.post(`/projectlookup/projectObjectiveAccomplishmentTargetSelection/${params.projectId}`, params.selections).catch(
      (exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      },
    );
    commit('SET_PROJECT_OBJECTIVE_ACCOMPLISHMENT_TARGET_SELECTIONS', params.selections);
  },
  async setProjectObjectiveSelectionsAsync({ commit }, params) {
    await api.post(`/projectlookup/projectObjectiveSelection/${params.projectId}`, params.selections).catch(
      (exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      },
    );
    commit('SET_PROJECT_OBJECTIVE_SELECTIONS', params.selections);
  },
  async deleteProjectAccomplishmentTargetMonitoringEvent({ commit }, id) {
    await api.delete(`/projectlookup/projectAccomplishmentTargetMonitoringEvent/${id}`).catch(
      (exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      },
    );
  },
  async getProjectPractices({ commit }, projectId) {
    await api.get(`/projectpractice/project/${projectId}`).then((response) => {
      if (response.status === 200) {
        commit('SET_SUCCESS_MESSAGE', 'Project practices were successfully read.');
      }
      commit('SET_PROJECT_LOADING', false);
      return commit('SET_PROJECT_PRACTICES', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async postProjectPractice({ commit }, projectPractice) {
    await api.post('/projectpractice', projectPractice).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async putProjectPractice({ commit }, projectPractice) {
    await api.put('/projectpractice', projectPractice).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async deleteProjectPractice({ commit }, id) {
    await api.delete(`/projectpractice/${id}`).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getPractices({ commit }) {
    return api.get('/practice').then(
      (response) => commit('SET_PRACTICES', response.data),
    ).catch(() => {
      console.error('No practices found');
      return null;
    });
  },
  async getPracticeComponents({ commit }) {
    return api.get('/practicecomponent').then(
      (response) => commit('SET_PRACTICE_COMPONENTS', response.data),
    ).catch(() => {
      console.error('No practice components found');
      return null;
    });
  },
  async getTargetSeasons({ commit }) {
    return api.get('/managementPlan/targetSeasons').then(
      (response) => commit('SET_TARGET_SEASONS', response.data),
    ).catch(() => {
      console.error('No target seasons found');
      return null;
    });
  },
  async getIncentiveTypes({ commit }) {
    await api.get('/projectlookup/incentivetype').then(
      (response) => {
        commit('SET_INCENTIVE_TYPES', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectIncentivePayments({ commit }, projectId) {
    await api.get(`/projectincentivepayment/project/${projectId}`).then(
      (response) => {
        commit('SET_PROJECT_INCENTIVE_PAYMENTS', response.data);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async postProjectIncentivePayment({ commit }, projectIncentivePayment) {
    await api.post('/projectincentivepayment', projectIncentivePayment).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async putProjectIncentivePayment({ commit }, projectIncentivePayment) {
    await api.put('/projectincentivepayment', projectIncentivePayment).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async deleteProjectIncentivePayment({ commit }, id) {
    await api.delete(`/projectincentivepayment/${id}`).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectConcept({ commit }, projectId) {
    return api.get(`/projectconcept/${projectId}`).then(
      (response) => commit('SET_PROJECT_CONCEPT', response.data),
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async postProjectConcept({ commit }, projectConcept) {
    await api.post('/projectConcept', projectConcept).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async putProjectConcept({ commit }, projectConcept) {
    await api.put('/projectConcept', projectConcept).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getFundsPerFiscalYear({ commit, state }, fiscalYear) {
    if (state.fundsPerFiscalYear.year !== fiscalYear || state.fundsPerFiscalYear.funds.length === 0) {
      return api.get(`/project/fundsPerFiscalYear/${fiscalYear}`).then(
        (response) => commit('SET_FUNDS_PER_FISCAL_YEAR', { year: fiscalYear, funds: response.data }),
      ).catch((exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      });
    }
    return state.fundsPerFiscalYear;
  },
  async getFundingSourceEncumberedFunds({ commit }, fiscalYear) {
    return api.get(`/fundingSourceEncumberedFunds/fiscalYear/${fiscalYear}`).then(
      (response) => commit('SET_FUNDING_SOURCE_ENCUMBERED_FUNDS', response.data),
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getEncumberedFundsOnApprovedBudgets({ commit }, fiscalYear) {
    return api.get(`/fundingSourceEncumberedFunds/fiscalYearApprovedBudgets/${fiscalYear}`).then(
      (response) => commit('SET_ENCUMBERED_FUNDS_APPROVED_BUDGETS', response.data),
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getFundingSourceExpendedFunds({ commit }, fiscalYear) {
    return api.get(`/fundingSourceExpendedFunds/fiscalYear/${fiscalYear}`).then(
      (response) => commit('SET_FUNDING_SOURCE_EXPENDED_FUNDS', response.data),
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectWorkorders({ commit }, projectId) {
    return api.get(`/workorder/project/${projectId}`).then(
      (response) => {
        // const data = response.data;
        const projectWorkorders = response.data.map((item) => {
          const projectWorkorder = { ...item };
          projectWorkorder.flgUpdating = false;
          return projectWorkorder;
        });
        return commit('SET_PROJECT_WORKORDERS', projectWorkorders);
      },
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectWorkorderBills({ commit }, ids) {
    const params = ids.map((id) => `ids=${id}`).join('&');
    if (ids.length === 1 && ids[0] === undefined) {
      commit('SET_SERVER_ERROR', 'No project workorder ids were provided.');
    }
    return api.get(`/workorderbill/workorder?${params}`).then((response) => {
      const data = response.data.sort((a, b) => b.receivedDate.localeCompare(a.receivedDate));
      let projectWorkOrderBills = [];
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Received'),
      );
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Submitted for Approval'),
      );
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Submitted for Payment'),
      );
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Approved for Payment'),
      );
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Paid'),
      );
      projectWorkOrderBills = projectWorkOrderBills.concat(
        data.filter((e) => e.status === 'Void'),
      );
      commit('SET_PROJECT_WORKORDER_BILLS', projectWorkOrderBills);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async postProjectWorkorderBill({ commit }, bill) {
    await api.post('/workorderbill', bill).then(
      (response) => commit('SET_PROJECT_WORKORDER_BILL', response.data),
    ).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async putProjectWorkorderBill({ commit }, bill) {
    await api.put('/workorderbill', bill).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async putProjectWorkorderBillStatus({ commit }, params) {
    await api.put(`/workorderbill/${params.id}/status/${params.status}`).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async postProjectWorkorderBillLineItems({ commit }, params) {
    while (params.workOrderBillLineItems && params.workOrderBillLineItems.length > 0) {

      const lineItem = params.workOrderBillLineItems.pop();

      const lineItemModel = {
        id: lineItem.id,
        workOrderBillId: params.workOrderBillId,
        workOrderLineItemId: lineItem.workOrderLineItemId,
        fundingSourceId: lineItem.fundingSourceId,
        quantity: Number(lineItem.quantity),
        salePrice: Number(lineItem.salePrice),
        billingComplete: lineItem.billingComplete,
      };

      await api.post(
        '/workorderbilllineitem',
        lineItemModel,
      ).catch((exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      });
    }
  },
  async postProjectWorkorderBillIncentivePayments({ commit }, params) {
    while (params.workOrderBillIncentives && params.workOrderBillIncentives.length > 0) {

      const incentivePayment = params.workOrderBillIncentives.pop();

      const incentivePaymentModel = {
        id: null,
        workOrderBillId: params.workOrderBillId,
        workOrderIncentivePaymentId: incentivePayment.id,
        fundingSourceId: incentivePayment.fundingSourceId,
        amountBilled: Number(incentivePayment.amountBilled),
      };

      await api.post(
        '/workorderbillincentivepayment',
        incentivePaymentModel,
      ).catch((exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      });
    }
  },
  async putProjectWorkorderBillLineItems({ commit }, lineItems) {
    while (lineItems && lineItems.length > 0) {

      const lineItem = lineItems.pop();

      const lineItemModel = {
        id: lineItem.id,
        workOrderBillId: lineItem.workOrderBillId,
        workOrderLineItemId: lineItem.workOrderLineItemId,
        fundingSourceId: lineItem.fundingSourceId,
        quantity: Number(lineItem.quantity),
        salePrice: Number(lineItem.salePrice),
        billingComplete: lineItem.billingComplete,
      };

      await api.put(
        '/workorderbilllineitem',
        lineItemModel,
      ).catch((exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      });
    }
  },
  async putProjectWorkorderBillIncentivePayments({ commit }, incentivePayments) {
    while (incentivePayments && incentivePayments.length > 0) {
      const incentivePayment = incentivePayments.pop();

      const incentivePaymentModel = {
        id: incentivePayment.id,
        workOrderBillId: incentivePayment.workOrderBillId,
        workOrderIncentivePaymentId: incentivePayment.workOrderIncentivePaymentId,
        fundingSourceId: incentivePayment.fundingSourceId,
        amountBilled: Number(incentivePayment.amountBilled),
      };

      await api.put(
        '/workorderbillincentivepayment',
        incentivePaymentModel,
      ).catch((exception) => {
        console.error(exception);
        commit('SET_SERVER_ERROR', exception);
      });
    }
  },
  async getProjectWorkorderLineItemFundingSources({ commit }, ids) {
    const params = ids.map((id) => `ids=${id}`).join('&');
    await api.get(`/workorderlineitemfundingsource/workorder?${params}`).then(async (response) => {
      commit('SET_PROJECT_WORKORDER_LINEITEM_FUNDINGSOURCES', response.data);
    }).catch((exception) => {
      console.error(exception);
      commit('SET_SERVER_ERROR', exception);
    });
  },
  async getProjectBudgetYearSnapshots({ commit }, projectId) {
    await api.get(`/project/${projectId}/budgetYearSnapshots`).then(
      (response) => commit('SET_PROJECT_BUDGET_YEAR_SNAPSHOTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getProjectsPendingApproval({ commit }) {
    await api.get('/project/projectsPendingApproval').then(
      async (response) => commit('SET_PROJECTS_PENDING_APPROVAL', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  async getMyActiveProjects({ commit }) {
    await api.get('/project/myActiveProjects').then(
      async (response) => commit('SET_MY_ACTIVE_PROJECTS', response.data),
    ).catch((error) => {
      console.error(error);
      commit('SET_SERVER_ERROR', error);
    });
  },
  replaceProjectWorkorder({ commit }, workorder) {
    commit('REPLACE_PROJECT_WORKORDER', workorder);
  },
  setPlanDetailsLoading({ commit }, planDetailsLoading) {
    commit('SET_PLAN_DETAILS_LOADING', planDetailsLoading);
  },
};

// WorkOrderOutputModel has no WorkOrderBills property, so work orders always arrive from the API
// without one - it is grafted on client-side by loadProjectWorkorderBills(). Everything that
// reads workOrderBills expects an array, so hold that invariant here, at the only two places that
// write into state.projectWorkorders. Without this, any refetch of the work orders behind
// ProjectWorkorders.load()'s back leaves workOrderBills undefined.
function withWorkOrderBills(state, workorder) {
  if (workorder.workOrderBills) {
    return workorder;
  }
  return {
    ...workorder,
    workOrderBills: (state.projectWorkorderBills || []).filter(
      (e) => e.workOrderId === workorder.id,
    ),
  };
}

export const mutations = {
  SET_DASHBOARD_DATA(state, data) {
    state.dashboardData = data;
  },
  SET_DASHBOARD_LOADING(state, dataLoading) {
    state.dashboardLoading = dataLoading;
  },
  SET_PENDING_BUDGETS(state, data) {
    state.pendingBudgets = data;
  },
  SET_FUNDING_SOURCES_LOADING(state, fundingSourcesLoading) {
    state.fundingSourcesLoading = fundingSourcesLoading;
  },
  SET_PROJECT(state, project) {
    state.project = project;
  },
  SET_PROJECT_LOADING(state, projectLoading) {
    state.projectLoading = projectLoading;
  },
  SET_PROJECT_GEOMETRY(state, projectGeometry) {
    state.projectGeometry = projectGeometry;
  },
  SET_PROJECT_SITES(state, projectSites) {
    if (!projectSites) {
      state.projectSites = projectSites;
    } else {
      state.projectSites = projectSites
        .sort((a, b) => a.relatedSite.siteName.localeCompare(b.relatedSite.siteName));
    }
  },
  SET_PROJECT_SITES_LOADING(state, projectSitesLoading) {
    state.projectSitesLoading = projectSitesLoading;
  },
  SET_PLAN_DETAILS(state, planDetails) {
    state.currentPlanDetails = planDetails;
    state.currentPlanDetails.category = state.project.category;
  },
  SET_PROJECT_BUDGET(state, projectBudget) {
    state.projectBudget = projectBudget;
    state.projectBudget.projectBudgetYears = state.projectBudget
      .projectBudgetYears.sort((a, b) => a.year - b.year);
  },
  SET_PROJECT_BUDGET_SUMMARY_DATA_LOADED(state, dataLoaded) {
    state.projectBudgetSummaryDataLoaded = dataLoaded;
  },
  SET_PROJECT_BUDGET_YEAR_SNAPSHOTS(state, projectBudgetYearSnapshots) {
    state.projectBudgetYearSnapshots = projectBudgetYearSnapshots;
  },
  SET_EXISTING_BUDGET_LINE_ITEM(state, lineItem) {
    const year = state.projectBudget
      .projectBudgetYears.find((pby) => pby.id === lineItem.projectBudgetYearId);
    year.projectBudgetLineItems = year.projectBudgetLineItems.filter((li) => li.id !== lineItem.id);
    year.projectBudgetLineItems.push(lineItem);
  },
  SET_PROJECT_BUDGET_LINE_ITEM(state, lineItem) {
    const year = state.projectBudget
      .projectBudgetYears.find((pby) => pby.id === lineItem.projectBudgetYearId);
    year.projectBudgetLineItems.push(lineItem);
  },
  SET_PROJECT_BUDGET_YEAR_CONTINGENCY(state, year) {
    const selectedYear = state.projectBudget
      .projectBudgetYears.find((pby) => pby.id === year.id);
    selectedYear.contingencyPercent = year.contingencyPercent;
  },
  DELETE_PROJECT_BUDGET_LINE_ITEM(state, lineItem) {
    const year = state.projectBudget
      .projectBudgetYears.find((pby) => pby.id === lineItem.projectBudgetYearId);
    year.projectBudgetLineItems
      .splice(year.projectBudgetLineItems.indexOf(lineItem), 1);
  },
  SET_PRICELISTS(state, priceLists) {
    state.priceLists = priceLists;
  },
  SET_FUNDING_SOURCES(state, fundingSources) {
    state.fundingSources = fundingSources.filter(
      (fs) => {
        const active = fs.customfields.find((cf) => cf.fielD_NAME === 'ACTIVE__c');
        if (!active) {
          return false;
        }
        return active.fielD_VALUE === true;
      },
    );
  },
  SET_FUNDS_PER_FISCAL_YEAR(state, fundsPerFiscalYear) {
    state.fundsPerFiscalYear = fundsPerFiscalYear;
  },
  SET_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_CURRENT_PRICE_LISTS(state, priceLists) {
    state.currentPriceLists = priceLists.map(
      ({ priceList, entry }) => ({ ...priceList, entry }),
    );
  },
  SET_ANNUAL_HISTORY_IS_LOADING(state, id) {
    Vue.set(state.loadingAnnualBudgetHistoryItems, id, true);
  },
  SET_ANNUAL_HISTORY_NOT_LOADING(state, id) {
    Vue.set(state.loadingAnnualBudgetHistoryItems, id, false);
  },
  SET_ANNUAL_HISTORY_ITEMS(state, { id, historyItems }) {
    Vue.set(state.annualBudgetHistoryItems, id, historyItems);
  },
  SET_CURRENT_PRICE_LISTS_LOADING(state, value) {
    state.currentPriceListsLoading = value;
  },
  SET_SUCCESS_MESSAGE(state, successMessage) {
    state.successMessage = successMessage;
  },
  SET_ERROR_MESSAGE(state, errorMessage) {
    state.errorMessage = errorMessage;
  },
  SET_DECREASE_SUCCESS_MESSAGE(state, successMessage) {
    state.decreaseSuccessMessage = successMessage;
  },
  SET_DECREASE_ERROR_MESSAGE(state, errorMessage) {
    state.decreaseErrorMessage = errorMessage;
  },
  SET_SERVER_ERROR(state, serverError) {
    state.serverError = serverError;
  },
  ADD_SITE_CONTACTS_LOADING(state, siteId) {
    state.siteContactsLoading.push(siteId);
  },
  REMOVE_SITE_CONTACTS_LOADING(state, siteId) {
    const index = state.siteContactsLoading.indexOf(siteId);
    if (index > -1) {
      state.siteContactsLoading.splice(index, 1);
    }
  },
  CLEAR_PROJECT_CONTACTS(state) {
    state.projectContacts = [];
  },
  ADD_PROJECT_CONTACTS(state, siteContacts) {
    state.projectContacts.push(siteContacts);
  },
  ADD_SITE_ORGANIZATIONS_LOADING(state, siteId) {
    state.siteOrganizationsLoading.push(siteId);
  },
  REMOVE_SITE_ORGANIZATIONS_LOADING(state, siteId) {
    const index = state.siteOrganizationsLoading.indexOf(siteId);
    if (index > -1) {
      state.siteOrganizationsLoading.splice(index, 1);
    }
  },
  CLEAR_PROJECT_ORGANIZATIONS(state) {
    state.projectOrganizationations = [];
  },
  ADD_PROJECT_ORGANIZATIONS(state, siteOrganizations) {
    state.projectOrganizations.push(siteOrganizations);
  },
  SET_PROJECT_RESOURCE_PRIORITIES(state, data) {
    state.projectResourcePriorities = data;
  },
  SET_PROJECT_ACCOMPLISHMENT_TARGET_MONITORING_EVENTS(state, data) {
    state.projectAccomplishmentTargetMonitoringEvents = data;
  },
  SET_PROJECT_ACCOMPLISHMENT_TARGET_MONITORING_EVENT_EVALUATION_METHODS(state, data) {
    state.projectAccomplishmentTargetMonitoringEventEvaluationMethods = data;
  },
  SET_PROJECT_GOALS(state, data) {
    state.projectGoals = data;
  },
  SET_PROJECT_GOAL_SELECTIONS(state, data) {
    state.projectGoalSelections = data;
  },
  SET_PROJECT_ACCOMPLISHMENT_TARGETS(state, data) {
    state.projectAccomplishmentTargets = data;
  },
  SET_PROJECT_OBJECTIVE_ACCOMPLISHMENT_TARGET_SELECTIONS(state, data) {
    state.projectObjectiveAccomplishmentTargetSelections = data;
  },
  SET_PROJECT_OBJECTIVES(state, data) {
    state.projectObjectives = data;
  },
  SET_PROJECT_OBJECTIVE_SELECTIONS(state, data) {
    state.projectObjectiveSelections = data;
  },
  SET_PROJECT_PRACTICES(state, data) {
    state.projectPractices = data;
  },
  SET_PRACTICES(state, practices) {
    state.practices = practices;
  },
  SET_PRACTICE_COMPONENTS(state, practiceComponents) {
    state.practiceComponents = practiceComponents;
  },
  SET_TARGET_SEASONS(state, targetSeasons) {
    state.targetSeasons = targetSeasons;
  },
  SET_INCENTIVE_TYPES(state, data) {
    state.incentiveTypes = data;
  },
  SET_PROJECT_INCENTIVE_PAYMENTS(state, data) {
    state.projectIncentivePayments = data;
  },
  SET_PROJECT_CONCEPT(state, data) {
    state.projectConcept = data;
  },
  SET_FUNDING_SOURCE_ENCUMBERED_FUNDS(state, data) {
    state.fundingSourceEncumberedFunds = data;
  },
  SET_FUNDING_SOURCE_EXPENDED_FUNDS(state, data) {
    state.fundingSourceExpendedFunds = data;
  },
  SET_ENCUMBERED_FUNDS_APPROVED_BUDGETS(state, data) {
    state.encumberedFundsForApprovedBudgets = data;
  },
  SET_PROJECT_SNAPSHOT(state, data) {
    state.projectSnapshot = data;
  },
  SET_PROJECT_SNAPSHOTS(state, data) {
    state.projectSnapshots = data;
  },
  SET_PROJECT_DRAFT_SNAPSHOT(state, data) {
    state.projectSnapshotDraft = data;
  },
  SET_PROJECT_DRAFT_SNAPSHOT_LOADING(state, projectSnapshotDraftLoading) {
    state.projectSnapshotDraftLoading = projectSnapshotDraftLoading;
  },
  SET_CANCELLED_COMPLETED_PROJECT_SNAPSHOTS(state, data) {
    state.cancelledCompletedProjectSnapshots = data;
  },
  SET_PROJECT_FISCALYEAR_SNAPSHOTS(state, data) {
    state.projectFiscalYearSnapshots = data;
  },
  SET_CURRENT_PROJECT_SNAPSHOTS(state, data) {
    state.currentProjectSnapshots = data;
  },
  SET_PROJECT_WORKORDERS(state, data) {
    state.projectWorkorders = (data || []).map((e) => withWorkOrderBills(state, e));
  },
  SET_PROJECT_WORKORDER_BILL(state, data) {
    state.projectWorkorderBill = data;
  },
  SET_PROJECT_WORKORDER_BILLS(state, data) {
    state.projectWorkorderBills = data;
  },
  SET_PROJECT_WORKORDER_LINEITEM_FUNDINGSOURCES(state, data) {
    state.projectWorkorderLineItemFundingSources = data;
  },
  SET_HAS_OPEN_WORK_ORDERS(state, data) {
    state.hasOpenWorkOrders = data;
  },
  SET_HAS_OPEN_WORK_ORDER_BILLS(state, data) {
    state.hasOpenWorkOrderBills = data;
  },
  SET_PROJECTS_PENDING_APPROVAL(state, data) {
    state.projectsPendingApproval = data;
  },
  SET_MY_ACTIVE_PROJECTS(state, data) {
    state.myActiveProjects = data;
  },
  REPLACE_PROJECT_WORKORDER(state, data) {
    const workorder = withWorkOrderBills(state, data);
    const index = state.projectWorkorders.findIndex((e) => e.id === workorder.id);
    if (index >= 0) {
      // splice, not `[index] = workorder`: Vue 2 cannot observe assignment by index, so the
      // work order card kept rendering the values it was replacing.
      state.projectWorkorders.splice(index, 1, workorder);
    } else {
      state.projectWorkorders.push(workorder);
    }
  },
  SET_PLAN_DETAILS_LOADING(state, planDetailsLoading) {
    state.planDetailsLoading = planDetailsLoading;
  },
};

export const getters = {
  fundingSourceMap: (state) => {
    if (!state.projectWorkorders || !state.projectIncentivePayments) {
      return new Map();
    }
    const workOrderLineItems = state.projectWorkorders
      .map((e) => e.workOrderLineItems)
      .flat();
    const fundingSourceIds = workOrderLineItems.map((e) => e.fundingSourceId);

    const incentiveFundingSourceIds = state.projectIncentivePayments.map((e) => e.fundingSourceId);

    const allFundingSourceIds = fundingSourceIds.concat(incentiveFundingSourceIds);

    return new Map(state.fundingSources
      .filter((e) => allFundingSourceIds.includes(e.recorD_ID))
      .map((key) => [key.recorD_ID.toString(), key.recorD_NAME]));
  },
  paidWorkorderBillLineItems: (state, localGetters, rootState) => {
    if (!state.projectWorkorderBills) {
      return [];
    }
    return state.projectWorkorderBills
      .filter((e) => e.status === rootState.config.workOrderBillForm.submitStatus.Paid)
      .map((e) => e.workOrderBillLineItems)
      .flat()
      .map((e) => ({ fundingSourceId: e.fundingSourceId, itemCost: e.itemCost }));
  },
};
